package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.documents.DocumentOpener
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.domain.model.InvalidDocumentException
import com.evoapps.evoshift.domain.model.PolicyAcknowledgement
import com.evoapps.evoshift.domain.model.PolicyReadReport
import com.evoapps.evoshift.domain.model.PolicyStatus
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.WorkplacePolicy
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.documents.AcknowledgePolicyUseCase
import com.evoapps.evoshift.domain.usecase.documents.DeletePolicyUseCase
import com.evoapps.evoshift.domain.usecase.documents.GetPoliciesUseCase
import com.evoapps.evoshift.domain.usecase.documents.GetPolicyReadReportUseCase
import com.evoapps.evoshift.domain.usecase.documents.OpenPolicyUseCase
import com.evoapps.evoshift.domain.usecase.documents.PickedFile
import com.evoapps.evoshift.domain.usecase.documents.PublishPolicyUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toDocumentUiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.documents_file_unreadable
import evoshift.composeapp.generated.resources.policies_confirmed
import evoshift.composeapp.generated.resources.policies_deleted
import evoshift.composeapp.generated.resources.policies_published
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock

/** A picked PDF waiting for its title (the publish dialog). */
data class PolicyDraft(val file: PickedFile, val title: String = "")

data class PoliciesUiState(
    val isLoading: Boolean = true,
    val policies: List<PolicyStatus> = emptyList(),
    /** Managers: how many of the current team confirmed each policy (policy id → confirmed / team). */
    val readCounts: Map<String, Pair<Int, Int>> = emptyMap(),
    val canConfirm: Boolean = false,
    val canPublish: Boolean = false,
    val canSeeReport: Boolean = false,
    val draft: PolicyDraft? = null,
    val publishing: Float? = null,
    /** The policy waiting for "I have read it" in the confirmation dialog. */
    val confirming: WorkplacePolicy? = null,
    /** Who confirmed and who has not (managers' dialog). */
    val report: PolicyReadReport? = null,
    val openingId: String? = null,
    val message: UiText? = null,
    val error: UiText? = null,
    val readerId: String? = null
)

/**
 * SH-122: the workplace's policies. Everyone opens them and confirms "I have read it" (stamped by
 * the server; a confirmation of reading, not a qualified electronic signature); managers publish
 * PDFs, remove them and see who confirmed and when. A confirmation shows at once and is undone if
 * the server refuses it.
 */
class PoliciesViewModel(
    private val workplaceId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getPolicies: GetPoliciesUseCase,
    private val publishPolicy: PublishPolicyUseCase,
    private val deletePolicy: DeletePolicyUseCase,
    private val openPolicy: OpenPolicyUseCase,
    private val acknowledgePolicy: AcknowledgePolicyUseCase,
    private val getReadReport: GetPolicyReadReportUseCase,
    private val opener: DocumentOpener,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val clock: Clock = AppClock
) : ViewModel() {
    private val _uiState = MutableStateFlow(PoliciesUiState())
    val uiState: StateFlow<PoliciesUiState> = _uiState.asStateFlow()
    private val job = JobSlot()

    init {
        job.launch(viewModelScope) {
            combine(getCurrentUser(), observePlanActive.activeAt(workplaceId)) { user, planActive -> user to planActive }
                .distinctUntilChanged()
                .collect { (user, planActive) -> load(user, planActive) }
        }
    }

    private suspend fun load(user: User?, planActive: Boolean) {
        val capabilities = user?.capabilitiesAt(workplaceId, planActive).orEmpty()
        if (user == null || !capabilities.shows(Gate.POLICIES)) {
            _uiState.value = PoliciesUiState(isLoading = false)
            return
        }
        _uiState.update {
            it.copy(
                readerId = user.uid,
                canConfirm = capabilities.shows(Gate.CONFIRM_POLICY),
                canPublish = capabilities.shows(Gate.POLICIES_EDITING),
                canSeeReport = capabilities.shows(Gate.POLICY_READ_REPORT)
            )
        }
        refresh()
    }

    private suspend fun refresh() {
        val uid = _uiState.value.readerId ?: return
        getPolicies(workplaceId, uid)
            .onSuccess { list ->
                _uiState.update { it.copy(isLoading = false, policies = list) }
                if (_uiState.value.canSeeReport) loadCounts(list.map { it.policy })
            }
            .onFailure { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
    }

    private suspend fun loadCounts(policies: List<WorkplacePolicy>) {
        val counts = policies.mapNotNull { policy ->
            getReadReport(policy).getOrNull()?.let { report -> policy.id to (report.confirmedOfTeam to report.teamSize) }
        }.toMap()
        _uiState.update { it.copy(readCounts = counts) }
    }

    // --- Reading and confirming ---------------------------------------------------------------

    fun open(policy: WorkplacePolicy) {
        if (_uiState.value.openingId != null) return
        _uiState.update { it.copy(openingId = policy.id) }
        viewModelScope.launch {
            openPolicy(policy)
                .mapCatching { bytes -> opener.open(policy.fileName, DocumentFiles.PDF, bytes) }
                .onFailure { e -> _uiState.update { it.copy(error = e.toDocumentUiText()) } }
            _uiState.update { it.copy(openingId = null) }
        }
    }

    fun askToConfirm(policy: WorkplacePolicy) = _uiState.update { it.copy(confirming = policy) }

    fun cancelConfirm() = _uiState.update { it.copy(confirming = null) }

    /** "I have read it": shown at once, the server's time replaces the device's. */
    fun confirm() {
        val state = _uiState.value
        val policy = state.confirming ?: return
        val uid = state.readerId ?: return
        val before = state.policies
        val optimistic = PolicyAcknowledgement(policy.id, workplaceId, uid, clock.now())
        _uiState.update { s ->
            s.copy(confirming = null, policies = s.policies.map { if (it.policy.id == policy.id) it.copy(acknowledgement = optimistic) else it })
        }
        viewModelScope.launch {
            acknowledgePolicy(policy)
                .onSuccess { ack ->
                    _uiState.update { s ->
                        s.copy(
                            policies = s.policies.map { if (it.policy.id == policy.id) it.copy(acknowledgement = ack) else it },
                            message = UiText.Resource(Res.string.policies_confirmed)
                        )
                    }
                    if (_uiState.value.canSeeReport) loadCounts(_uiState.value.policies.map { it.policy })
                }
                .onFailure { e -> _uiState.update { it.copy(policies = before, error = e.toUiText()) } }
        }
    }

    // --- Managers -----------------------------------------------------------------------------

    fun onPicked(file: PickedFile?) {
        if (file == null) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.documents_file_unreadable)) }
            return
        }
        DocumentFiles.problemOf(file.bytes, setOf(DocumentFiles.PDF))?.let { problem ->
            _uiState.update { it.copy(error = InvalidDocumentException(problem).toUiText()) }
            return
        }
        val suggested = file.name.orEmpty().substringAfterLast('/').substringBeforeLast('.').replace('_', ' ').trim()
        _uiState.update { it.copy(draft = PolicyDraft(file, suggested.take(DocumentFiles.MAX_POLICY_TITLE))) }
    }

    fun setDraftTitle(title: String) = _uiState.update { s -> s.copy(draft = s.draft?.copy(title = title.take(DocumentFiles.MAX_POLICY_TITLE))) }

    fun cancelDraft() = _uiState.update { it.copy(draft = null) }

    fun publish() {
        val state = _uiState.value
        val draft = state.draft ?: return
        val uid = state.readerId ?: return
        if (draft.title.isBlank() || state.publishing != null) return
        _uiState.update { it.copy(draft = null, publishing = 0f) }
        viewModelScope.launch {
            publishPolicy(workplaceId, draft.title, draft.file, uid) { progress -> _uiState.update { s -> s.publishing?.let { s.copy(publishing = progress) } ?: s } }
                .onSuccess { policy ->
                    _uiState.update { s ->
                        s.copy(
                            publishing = null,
                            policies = listOf(PolicyStatus(policy)) + s.policies,
                            message = UiText.Resource(Res.string.policies_published)
                        )
                    }
                    if (_uiState.value.canSeeReport) loadCounts(_uiState.value.policies.map { it.policy })
                }
                .onFailure { e -> _uiState.update { it.copy(publishing = null, error = e.toDocumentUiText()) } }
        }
    }

    fun delete(policy: WorkplacePolicy) {
        val before = _uiState.value.policies
        _uiState.update { s -> s.copy(policies = s.policies.filterNot { it.policy.id == policy.id }, report = null) }
        viewModelScope.launch {
            deletePolicy(policy)
                .onSuccess { _uiState.update { it.copy(message = UiText.Resource(Res.string.policies_deleted)) } }
                .onFailure { e -> _uiState.update { it.copy(policies = before, error = e.toDocumentUiText()) } }
        }
    }

    fun showReport(policy: WorkplacePolicy) {
        viewModelScope.launch {
            getReadReport(policy)
                .onSuccess { report -> _uiState.update { it.copy(report = report) } }
                .onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
        }
    }

    fun hideReport() = _uiState.update { it.copy(report = null) }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }
}
